'use client';

import React, { useState, useRef, useEffect } from 'react';

import { cn } from '@app/libs/utils';
import { Z_INDEX } from '@app/libs/zIndex';

interface TooltipProps {
  children: React.ReactNode;
  content: string | React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  delay?: number;
  variant?: 'default' | 'danger' | 'success' | 'warning' | 'info' | 'primary' | 'white' | 'none';
  trigger?: 'hover' | 'click';
  triggerClassName?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  position = 'top',
  className,
  variant = 'default',
  delay = 300,
  trigger = 'hover',
  triggerClassName,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number | undefined;
    left: number | undefined;
  }>({ top: undefined, left: undefined });
  const [arrowPosition, setArrowPosition] = useState<{
    top?: number;
    left?: number;
  }>({});
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<any>(null);

  const showTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  const updatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    let top = 0;
    let left = 0;

    switch (position) {
      case 'top':
        top = triggerRect.top - tooltipRect.height - 8;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case 'bottom':
        top = triggerRect.bottom + 8;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case 'left':
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.left - tooltipRect.width - 8;
        break;
      case 'right':
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.right + 8;
        break;
    }

    // Đảm bảo tooltip không bị tràn ra ngoài viewport (position: fixed)
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (left < 8) left = 8;
    if (left + tooltipRect.width > viewportWidth - 8) {
      left = viewportWidth - tooltipRect.width - 8;
    }
    if (top < 8) top = 8;
    if (top + tooltipRect.height > viewportHeight - 8) {
      top = viewportHeight - tooltipRect.height - 8;
    }

    // Tính toán vị trí arrow để luôn trỏ vào target
    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    const triggerCenterY = triggerRect.top + triggerRect.height / 2;
    const arrowSize = 8; // w-2 h-2 = 8px

    let arrowTop: number | undefined;
    let arrowLeft: number | undefined;
    let minArrowLeft;
    let maxArrowLeft;
    let minArrowTop;
    let maxArrowTop;

    switch (position) {
      case 'top':
      case 'bottom':
        // Arrow nằm ở cạnh trên/dưới, điều chỉnh theo trục X
        // Tính vị trí arrow để center của arrow thẳng hàng với center X của target
        arrowLeft = triggerCenterX - left - arrowSize / 2;
        // Giới hạn arrow trong tooltip (padding 12px mỗi bên từ px-3)
        minArrowLeft = 0;
        maxArrowLeft = tooltipRect.width - arrowSize;
        arrowLeft = Math.max(minArrowLeft, Math.min(maxArrowLeft, arrowLeft));
        break;
      case 'left':
      case 'right':
        // Arrow nằm ở cạnh trái/phải, điều chỉnh theo trục Y
        // Tính vị trí arrow để center của arrow thẳng hàng với center Y của target
        arrowTop = triggerCenterY - top - arrowSize / 2;
        // Giới hạn arrow trong tooltip (padding 8px mỗi bên từ py-2)
        minArrowTop = 0;
        maxArrowTop = tooltipRect.height - arrowSize;
        arrowTop = Math.max(minArrowTop, Math.min(maxArrowTop, arrowTop));
        break;
    }

    setTooltipPosition({ top, left });
    setArrowPosition({ top: arrowTop, left: arrowLeft });
  };

  useEffect(() => {
    if (isVisible) {
      updatePosition();
      const handleResize = () => updatePosition();
      const handleScroll = () => updatePosition();

      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll);

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isVisible, position]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const getArrowClasses = () => {
    const baseClasses = 'absolute w-2 h-2 transform rotate-45';

    switch (position) {
      case 'top':
        return `${baseClasses} -bottom-1`;
      case 'bottom':
        return `${baseClasses} -top-1`;
      case 'left':
        return `${baseClasses} -right-1`;
      case 'right':
        return `${baseClasses} -left-1`;
      default:
        return baseClasses;
    }
  };

  return (
    <div
      ref={triggerRef}
      className={cn('flex items-center', triggerClassName)}
      onClick={trigger === 'click' ? showTooltip : undefined}
      onMouseEnter={trigger === 'hover' ? showTooltip : undefined}
      onMouseLeave={trigger === 'hover' ? hideTooltip : undefined}
    >
      {children}
      {isVisible && (
        <div
          ref={tooltipRef}
          className={cn(
            'fixed z-50 px-3 py-2 text-sm text-white rounded-lg shadow-lg whitespace-nowrap',
            'animate-in fade-in-0 zoom-in-95 duration-200',
            variant === 'danger' && 'bg-red-500',
            variant === 'success' && 'bg-green-500',
            variant === 'warning' && 'bg-yellow-500',
            variant === 'info' && 'bg-gray-500',
            variant === 'default' && 'bg-gray-800',
            variant === 'primary' && 'bg-blue-500',
            variant === 'white' && 'bg-white text-gray-600',
            variant === 'none' && 'bg-gray-800 text-gray-200',
            className
          )}
          style={{
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            zIndex: Z_INDEX.tooltip,
          }}
        >
          {content}
          <div
            className={cn(
              getArrowClasses(),
              variant === 'default' && 'bg-gray-800',
              variant === 'primary' && 'bg-blue-500',
              variant === 'danger' && 'bg-red-500',
              variant === 'success' && 'bg-green-500',
              variant === 'warning' && 'bg-yellow-500',
              variant === 'info' && 'bg-gray-500',
              variant === 'white' && 'bg-white text-gray-600',
              variant === 'none' && 'bg-gray-800 text-gray-200'
            )}
            style={{
              ...(arrowPosition.left !== undefined && { left: `${arrowPosition.left}px` }),
              ...(arrowPosition.top !== undefined && { top: `${arrowPosition.top}px` }),
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Tooltip;
